import { navigateToSelectedChannelRoute } from 'utils/history';
import { FormattedMessage } from 'react-intl';
import { useSelectedLinkContext } from 'contexts';
import Route from 'routes/Routes';
import { type GridSize } from '@mui/material/Grid/Grid';
import { useQuery } from '@apollo/client';
import { GET_PARTNER_ACCOUNTS_WITH_FAMILY_PORTAL_QUERY, type GetPartnerAccountsWithFamilyPortalOutput } from 'graphql/partnerdocuments/PartnerDocumentsQueries';
import { type PartnerAccount } from 'types';
import MenuGridPageLayout, {MenuCategory} from 'layouts/MenuGridPageLayout';
import {teamviewer, documents, partner} from 'assets/category_cards/help';

export type HelpSubCategoryProps = {
  displayPartnerSection: boolean;
  subCategorySize: GridSize;
};

export const HelpPure = ({ displayPartnerSection, subCategorySize } : HelpSubCategoryProps) : JSX.Element => {
  const { selectedLink } = useSelectedLinkContext();
  
  const createNavigationClickHandler = (route: Route) => () => navigateToSelectedChannelRoute(
    selectedLink,
    route,
  );

  return (
    <MenuGridPageLayout title={<FormattedMessage id="category.help.description" />}>
      <MenuCategory
        altId='category.help.teamviewer'
        image={teamviewer}
        md={subCategorySize}
        onClick={createNavigationClickHandler(Route.TEAMVIEWER)}
      />

      <MenuCategory
        altId='category.help.documents.description'
        enableEvenIfNoSelectedLink
        image={documents}
        md={subCategorySize}
        onClick={createNavigationClickHandler(Route.HELP_DOCUMENTS)}
      />

      {displayPartnerSection && (
        <MenuCategory
          altId='partner.description'
          image={partner}
          md={subCategorySize}
          onClick={createNavigationClickHandler(Route.PARTNER_LIST)}
        />
      )}
    </MenuGridPageLayout>
  );
};

export const getHelpPureProps = (partnerAccounts: PartnerAccount[] | undefined): HelpSubCategoryProps => {
  if (partnerAccounts?.length) {
    return {
      displayPartnerSection: true,
      subCategorySize: 4,
    };
  } else {
    return {
      displayPartnerSection: false,
      subCategorySize: 6,
    };
  }
};

const Help = (): JSX.Element | null => {
  const { selectedLink } = useSelectedLinkContext();
  const { data, loading } = useQuery<GetPartnerAccountsWithFamilyPortalOutput>(
    GET_PARTNER_ACCOUNTS_WITH_FAMILY_PORTAL_QUERY,
    {
      skip: !selectedLink,
      variables: {
        channel: selectedLink?.channelKey,
      },
    },
  );

  if (loading) {
    return null;
  }

  const partnerAccounts = data?.getPartnerAccountsWithFamilyPortal?.partnerAccounts;

  const helpSubCategoryProps = getHelpPureProps(partnerAccounts);

  return <HelpPure {...helpSubCategoryProps} />;
};

export default Help;
